import React, { useMemo } from 'react'

import { SxProps } from '@mui/material'

import { TableComponents, TableVirtuoso, TableBodyProps } from 'react-virtuoso'

import Box from 'components_new/atoms/Box'
import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'
import Text from 'components_new/atoms/Text'

import MetricTableRow from 'components_new/organisms/MetricTableRow'

import Empty from './empty'

import { useMetrics } from 'redux/hooks/Metrics'
import { useAllowAccessMetric } from 'redux/hooks/Permissions'
import { useTranslation } from 'language/translator'

import { isCustomEconomyMetric, MetricStatus } from 'types/GlobalMetric'
import { TCoreWidgetSettingsLoaded } from 'types/GlobalWidget'

import { useAvailableMetrics } from './utils'
import { translateMetricStatus } from 'utils/enumTranslator'

interface Props {
  category: string | null
  displayInactiveMetrics?: boolean
  onClickMetric: (id: string, defaultVariant: string | null) => void
  search: string | null
  status?: string | null
  subcategory: string | null
  sx?: SxProps
  widget?: TCoreWidgetSettingsLoaded
}

const VirtuosoTableComponents: TableComponents<any> = {
  Table: (props) => (
    <Table
      {...props}
      sx={{
        /* 👇 make column widths obey the numbers you give them */
        tableLayout: 'fixed',

        /* 👇 keep the table the same width as its parent */
        width: '100%',
        maxWidth: '100%',
        'td, th': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }
      }}
    />
  ),
  TableBody: React.forwardRef<
    HTMLTableSectionElement,
    TableBodyProps & { context?: any }
  >(({ ...props }, ref) => <TableBody {...props} ref={ref} />),
  TableHead: ({ ref, ...props }) => <TableHead {...props} ref={ref} />,
  TableRow: React.forwardRef((props, ref) => {
    const allowed = useAllowAccessMetric(props.item?.id)

    return (
      <TableRow
        {...props}
        ref={ref}
        hover={allowed}
        onClick={() => (!allowed ? {} : props.item.onClick())}
        sx={{
          maxHeight: 64,
          cursor: !allowed ? undefined : 'pointer',
          opacity:
            !allowed || props.item.status !== MetricStatus.ACTIVE
              ? 0.5
              : undefined
        }}
      />
    )
  })
}

const MetricsTable = React.memo((props: Props) => {
  const {
    category,
    subcategory,
    displayInactiveMetrics = false,
    onClickMetric,
    search,
    status,
    sx,
    widget = null
  } = props

  const { t, lang } = useTranslation()
  const [metrics, loading] = useMetrics()

  // settings filtering
  const availableMetrics = useAvailableMetrics(
    metrics,
    displayInactiveMetrics,
    widget
  )

  const filteredMetrics = useMemo(() => {
    return availableMetrics
      .filter((metric) => {
        return (
          (!category || metric.category.slug === category) &&
          (!subcategory || metric.subcategory.slug === subcategory) &&
          (!status || translateMetricStatus[metric.status].slug === status) &&
          (!search ||
            metric.title[lang]
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase()))
        )
      })
      .map((metric) => ({
        ...metric,
        onClick: () =>
          onClickMetric(
            metric.id,
            isCustomEconomyMetric(metric) ? metric.default_variant : null
          )
      }))
  }, [availableMetrics, category, subcategory, status, search, onClickMetric])

  if (loading) {
    // TODO: loading
    return null
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        height: '100%',
        ...sx
      }}
    >
      <Text color="text.disabled" variant="caption">
        {t('global', 'shows')} <b>{filteredMetrics.length}</b>{' '}
        {t('global', 'of', true)} {availableMetrics.length}{' '}
        {t('metrics', 'metrics', true)}
      </Text>

      <Box sx={{ flex: 1 }}>
        {filteredMetrics.length === 0 ? (
          <Empty search={search} />
        ) : (
          <TableVirtuoso
            data={filteredMetrics}
            components={VirtuosoTableComponents}
            fixedHeaderContent={() => (
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell padding="none" sx={{ width: '55%' }}>
                  {t('metrics', 'metric')}
                </TableCell>
                <TableCell sx={{ width: '15%' }}>
                  {t('global', 'unit')}
                </TableCell>
                <TableCell sx={{ width: '15%' }}>
                  {category
                    ? t('global', 'subcategory')
                    : t('global', 'category')}
                </TableCell>
                <TableCell sx={{ width: '15%' }}>
                  {t('global', 'status')}
                </TableCell>
                <TableCell padding="checkbox">{''}</TableCell>
              </TableRow>
            )}
            itemContent={(_, item) => (
              <MetricTableRow
                metric={item}
                widget={widget}
                search={search}
                category={category}
                key={item.id}
              />
            )}
            style={{
              height: '100%',
              width: '100%'
            }}
          />
        )}
      </Box>
    </Box>
  )
})

export default MetricsTable
